/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useHandCombinationTranslation } from '../../hooks/useHandCombinationTranslation'
import { useRussianPokerStore } from '../../providers/RussianPokerProvider'
import { numberWithSpaces } from '../../utils/number'
import { DepositIcon } from '../Icons'
import { Typography } from '../Typography'
import {
  HeaderItem,
  PayoutBlock,
  PayoutBlockList,
  PayoutBlockListItem,
} from './styles'
import { BgMixin } from '../BettingBox/styles'

export const PaymentsTable: FC<{
  show: boolean
}> = observer(({ show }) => {
  const {
    game: {
      config: { limit, payout, bonus },
    },
  } = useRussianPokerStore()
  const { min, max, maxPayout } = limit
  const { t } = useTranslation()
  const { getCombinationLabel } = useHandCombinationTranslation()

  const icon = (
    <DepositIcon
      size={{
        mobile: '4px',
        tablet: '8px',
        desktop: '8px',
      }}
      color="gold1"
    />
  )

  return (
    <div
      css={css`
        border-radius: 4px;
        ${BgMixin};
        padding: 5px 7px;
        display: ${show ? 'block' : 'none'};
        z-index: 2;
      `}>
      <div>
        <div
          css={css`
            display: flex;
            gap: 11px;
            justify-content: space-between;
          `}>
          <HeaderItem>
            <Typography variant="text5" color="gold3" as="span">
              {t('shortMinBet')}
            </Typography>
            <Typography variant="text5" color="white" as="span">
              {numberWithSpaces(min)}
            </Typography>
            {icon}
          </HeaderItem>
          <HeaderItem>
            <Typography variant="text5" color="gold3" as="span">
              {t('shortMaxBet')}
            </Typography>
            <Typography variant="text5" color="white" as="span">
              {max === null ? 'unlim' : numberWithSpaces(max)}
            </Typography>
            {icon}
          </HeaderItem>
          <HeaderItem>
            <Typography variant="text5" color="gold3" as="span">
              {t('shortMaxPayout')}
            </Typography>
            <Typography variant="text5" color="white" as="span">
              {maxPayout === null ? 'unlim' : numberWithSpaces(maxPayout)}
            </Typography>
            {icon}
          </HeaderItem>
        </div>
      </div>
      <div
        css={css`
          width: 100%;
          height: 1px;
          margin: 7px 0;
          background-color: white;
          opacity: 0.1;
        `}
      />
      <div
        css={css`
          display: flex;
          gap: 15px;
          justify-content: space-between;
        `}>
        <PayoutBlock>
          <Typography variant="head2">{t('payoutByCombinations')}</Typography>
          <PayoutBlockList>
            {payout.data.length > 0 &&
              payout.data
                .slice()
                .filter((item) => !['HIGH_CARD'].includes(item.combination))
                .sort((a, b) => b.multiplier - a.multiplier)
                .map((item) => (
                  <PayoutBlockListItem
                    key={`${item.combination}:${item.multiplier}`}>
                    <Typography variant="text4">
                      {getCombinationLabel(item.combination)}
                    </Typography>
                    <Typography variant="text4">{item.multiplier}:1</Typography>
                  </PayoutBlockListItem>
                ))}
          </PayoutBlockList>
        </PayoutBlock>
        <PayoutBlock>
          <Typography variant="head2">{t('payoutByBonus')}</Typography>
          <PayoutBlockList>
            {bonus.payouts.length > 0 &&
              bonus.payouts
                .slice()
                .sort((a, b) => b.multiplier - a.multiplier)
                .map((item) => (
                  <PayoutBlockListItem
                    key={`${item.combination}:${item.multiplier}`}>
                    <Typography variant="text4">
                      {getCombinationLabel(item.combination)}
                    </Typography>
                    <Typography variant="text4">{item.multiplier}:1</Typography>
                  </PayoutBlockListItem>
                ))}
          </PayoutBlockList>
        </PayoutBlock>
      </div>
    </div>
  )
})
